import { useCallback, useEffect, useState } from 'react';

import { errorKey, type AuthApi, type Session } from '../auth/api';
import { useI18n } from '../i18n/I18nProvider';
import { Button } from '../ui/Button';
import { Sheet } from '../ui/Sheet';
import type { DownlinePage } from './api';
import './referral.css';

const PAGE = 50;

/**
 * 某一级的下级名单。
 *
 * 账号是**打过码的**（前六后四）。全量 uid 进分片键、进内部通道的 x-uid 头，
 * 开发环境下还直接当令牌用 —— 把整串发给另一个玩家等于发一份账号清单。
 */
export function DownlineSheet(
  { open, level, api, session, onClose }:
  {
    open: boolean; level: number; api: AuthApi;
    session: Session | null; onClose(): void;
  },
) {
  const { t, i18n } = useI18n();
  const [page, setPage] = useState<DownlinePage | null>(null);
  const [offset, setOffset] = useState(0);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    if (!open || !session) return;
    try {
      setPage(await api.downlines(session.token, level, offset, PAGE));
      setErr('');
    } catch (e) {
      setErr(t(errorKey(e)));
    }
  }, [api, session, level, offset, open, t]);

  useEffect(() => { void load(); }, [load]);
  // 换一级就回到第一页 —— 不回的话，从第 3 页切到一个只有 2 个人的层级，
  // 看到的是一片空白，而看起来像"这一级没人"。
  useEffect(() => { setOffset(0); }, [level]);

  const items = page?.items ?? [];
  const total = page?.total ?? 0;

  return (
    <Sheet open={open} onClose={onClose} title={t('ref.levelTitle', { n: level })}>
      {err && <p className="ref__err">{err}</p>}
      <p className="ref__sub">{t('ref.total', { n: total })}</p>

      {items.length === 0 && !err && <p className="ref__empty">{t('ref.noOne')}</p>}

      <ul className="ref__list">
        {items.map((d) => (
          <li key={d.id} className="ref__row">
            <span className="ref__who">
              <b>{d.nickname || t('ref.noName')}</b>
              <code>{d.id}</code>
            </span>
            <span className="ref__meta">
              <span className={d.effective ? 'ref__tag ref__tag--ok' : 'ref__tag'}>
                {t(d.effective ? 'ref.effective' : 'ref.notEffective')}
              </span>
              {d.ts !== null && <time>{i18n.dateTime(d.ts * 1000)}</time>}
            </span>
          </li>
        ))}
      </ul>

      {total > PAGE && (
        <div className="ref__pager">
          <Button variant="ghost" disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - PAGE))}>
            {t('ref.prev')}
          </Button>
          <span>{offset + 1}–{Math.min(offset + PAGE, total)} / {total}</span>
          <Button variant="ghost" disabled={offset + PAGE >= total}
                  onClick={() => setOffset(offset + PAGE)}>
            {t('ref.next')}
          </Button>
        </div>
      )}
    </Sheet>
  );
}
